// Add/remove chips for a string[] field (styleTags / generalTags / tourTags).
// tourTags are slugs (tour membership); we slugify those on entry. Others are
// kept as typed (trimmed). CONTRACTS §3 / §10.

import { useState } from "react";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export interface TagEditorProps {
  values: string[];
  onChange: (next: string[]) => void;
  /** Slugify entries (used for tourTags). */
  slug?: boolean;
  placeholder?: string;
  /** Optional datalist suggestions. */
  suggestions?: string[];
  inputId?: string;
}

export function TagEditor({
  values,
  onChange,
  slug = false,
  placeholder = "Add tag…",
  suggestions,
  inputId,
}: TagEditorProps) {
  const [draft, setDraft] = useState("");
  const listId = suggestions && inputId ? `${inputId}-list` : undefined;

  const add = () => {
    const norm = slug ? slugify(draft) : draft.trim();
    if (!norm) return;
    if (!values.includes(norm)) onChange([...values, norm]);
    setDraft("");
  };

  const remove = (tag: string) => onChange(values.filter((t) => t !== tag));

  return (
    <div className="tag-editor">
      <ul className="tag-list">
        {values.map((tag) => (
          <li key={tag} className="tag-chip">
            <span className="tag-chip__text">{tag}</span>
            <button
              type="button"
              className="tag-chip__remove"
              aria-label={`Remove ${tag}`}
              onClick={() => remove(tag)}
            >
              ×
            </button>
          </li>
        ))}
        {values.length === 0 && <li className="tag-list__empty">none</li>}
      </ul>
      <div className="tag-add">
        <input
          id={inputId}
          className="tag-add__input"
          type="text"
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          list={listId}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        {listId && suggestions && (
          <datalist id={listId}>
            {suggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        )}
        <button type="button" className="btn btn--small" onClick={add}>
          Add
        </button>
      </div>
    </div>
  );
}
